import type { ReactNode } from "react";

// eslint-disable-next-line import/no-unresolved
import { PersonaSwitcher } from "@astrana/ui/base";
import { useMutation } from "@apollo/client";

import { useCurrentUser } from "../auth/CurrentUserContext";
import { CURRENT_USER } from "../members/graphql/currentUser";
import {
  PERSONA_CHANGE,
  type PersonaChangeVars,
  type PersonaChangeResult,
} from "../members/graphql/personaChange";
import type { PersonaValue } from "../config/roles";

// "CARE_MANAGER" -> "Care Manager"
const humanize = (code: string): string =>
  code
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Gate for showing persona-group labels in the switcher (persona-groups epic):
 * true only when the user holds MORE THAN ONE persona (the switcher's own base
 * gate, `personas.length <= 1` below) AND at least one of them is assigned to a
 * group. Exported + pure so it's unit-testable without mounting the switcher.
 *
 * `@astrana/ui`'s PersonaSwitcher applies the same two-part gate internally, so
 * this is belt-and-braces — but deciding it here also means we never send group
 * data the switcher would just discard, and it keeps the rule visible on the app
 * side where the product decision lives.
 */
export function shouldShowPersonaGroups(
  personas: string[],
  personaGroups:
    | Partial<Record<string, { code: string; displayName: string }>>
    | undefined,
): boolean {
  // Tolerate a missing map rather than throwing. The context always supplies one
  // today, but this is read on the header's render path — a consumer holding an
  // older context shape (or a partial test double) must degrade to "no groups",
  // not crash the whole header.
  if (!personaGroups) return false;
  return personas.length > 1 && personas.some((p) => personaGroups[p] != null);
}

/**
 * Builds the persona switcher for the `ProfileMenu` `children` slot, or returns
 * `null` when the user has ≤1 persona. Returning `null` (rather than rendering
 * an element that itself renders null) matters: `ProfileMenu` gates its section
 * separator on the *prop*, so a truthy-but-empty child would leave an orphan
 * divider. Call this in the header and pass the result straight through.
 *
 * Owns the app-specific concerns the library primitive intentionally omits: the
 * persona query/context, the `PERSONA_CHANGE` mutation (or the dev offline
 * override), and the multi-persona gate.
 */
export function usePersonaSwitcher(): ReactNode {
  const {
    currentPersona,
    personas,
    personaLabels,
    personaGroups,
    devOverrideActive,
    setDevPersona,
  } = useCurrentUser();

  const [switchPersona, { loading }] = useMutation<
    PersonaChangeResult,
    PersonaChangeVars
  >(PERSONA_CHANGE, {
    refetchQueries: [{ query: CURRENT_USER }],
    awaitRefetchQueries: true,
  });

  // Nothing to switch between — omit the section entirely.
  if (personas.length <= 1) return null;

  const onSelect = (value: string) => {
    // Dev offline-override has no backend mutation — seed locally; production
    // fires PERSONA_CHANGE and refetches the user.
    if (devOverrideActive) {
      setDevPersona(value as PersonaValue);
    } else {
      void switchPersona({ variables: { personaCode: value } });
    }
  };

  // Only attach group data when the gate passes. Below the gate every entry is
  // group-less, which is byte-identical to the pre-persona-groups payload — so a
  // single-persona user, or one with no grouped personas, sees exactly what they
  // saw before.
  const showGroups = shouldShowPersonaGroups(personas, personaGroups);

  return (
    <PersonaSwitcher
      personas={personas.map((p) => {
        const group = showGroups ? personaGroups?.[p] : undefined;
        return {
          value: p,
          // The backend's own display string wins. `humanize` is the fallback for
          // a code the payload gave no displayName for, and for dev-override mode
          // (which has no backend to ask). It cannot produce the right answer for
          // a code it has never seen — "PowerUser" humanizes to "Poweruser", not
          // "Power User" — so a locally-derived label must never be preferred over
          // the server's.
          // Optional-chained for the same reason `shouldShowPersonaGroups`
          // tolerates a missing map: this runs on the header's render path, and a
          // consumer holding an older context shape (or a partial test double)
          // must degrade to the local label, not crash the header.
          label: personaLabels?.[p] ?? humanize(p),
          // The library names the display string `label`; our context calls it
          // `displayName`. Omit the key entirely rather than passing undefined.
          ...(group
            ? { group: { code: group.code, label: group.displayName } }
            : {}),
        };
      })}
      current={currentPersona}
      onSelect={onSelect}
      busy={loading}
    />
  );
}
